import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ANSWER_IDS, EXAMPLE_NOW } from './examples.js';
import { exampleTimePoint } from './examples-chronology.js';
/** The seeded ids of the link types the examples name. */
const LINK_TYPE_IDS = {
    contains: '49271519-e805-58ab-82b9-4cff5493d260',
    given_by: '0283aed9-0336-5118-aa04-d4e2f3a0953e',
    member_of: 'c4d0af4e-ba80-57f8-9ef2-a0a74021782f',
    related_to: '6c216a56-56a2-5fbe-8bbe-5eb9683f0b8c',
};
const UNBOUNDED = { empty: false, lo: null, hi: null };
function link(fields) {
    return {
        rv: 1,
        position: null,
        label: null,
        role: null,
        note: null,
        meta: {},
        isPrimary: false,
        origin: 'manual',
        fieldDefId: null,
        validFrom: null,
        validTo: null,
        ongoing: false,
        certainSpan: UNBOUNDED,
        possibleSpan: UNBOUNDED,
        spanStatus: 'ok',
        createdAt: EXAMPLE_NOW,
        updatedAt: EXAMPLE_NOW,
        ...fields,
    };
}
const ids = EXAMPLE_IDS;
/** Salt in the Wells is given by Captain Orla Venn. */
export const EXAMPLE_LINK = link({
    id: ids.linkSaltGivenByOrla.id,
    linkTypeId: LINK_TYPE_IDS.given_by,
    sourceId: ids.questSaltInTheWells.id,
    targetId: ids.npcOrlaVenn.id,
});
/** The link `links.create`'s example makes: Missing Lamplighters, related to Maelis Dorn. */
export const EXAMPLE_LINK_CREATED = {
    link: link({
        id: EXAMPLE_ANSWER_IDS.linkCreated,
        linkTypeId: LINK_TYPE_IDS.related_to,
        sourceId: ids.questMissingLamplighters.id,
        targetId: ids.npcMaelisDorn.id,
    }),
    demoted: null,
    warnings: [],
};
/**
 * A dated link (CS r3, deliverable 72; 1.C stage 2): Maelis Dorn is one of the Drowned Queen's
 * faithful (`member_of`, the type the deity's Worshippers field reads) from 9 Ember 1042 AL, with
 * no end yet. Beside the unchanged route example: `request` is a `links.create` the demo meets,
 * `response` the answer the mock gives it (it stores the boundaries and answers unbounded spans,
 * `spanStatus: 'ok'`; the server answers the spans the `spans` deriver writes).
 */
export const EXAMPLE_LINK_DATED = (() => {
    const validFrom = {
        timePoint: exampleTimePoint({ era: 'al', year: 1042, month: 'ember', day: 9 }, 'day', '9 Ember 1042'),
    };
    return {
        request: {
            params: { id: ids.npcMaelisDorn.id },
            body: {
                linkTypeKey: 'member_of',
                targetId: ids.deityDrownedQueen.id,
                validFrom,
                ongoing: true,
            },
        },
        response: {
            link: link({
                id: EXAMPLE_ANSWER_IDS.linkDated,
                linkTypeId: LINK_TYPE_IDS.member_of,
                sourceId: ids.npcMaelisDorn.id,
                targetId: ids.deityDrownedQueen.id,
                validFrom,
                ongoing: true,
            }),
            demoted: null,
            warnings: [],
        },
    };
})();
/** The arc's contains link to Salt in the Wells, moved to the front by `links.reorder`. */
export const EXAMPLE_LINK_REORDERED = link({
    id: ids.linkArcContainsSaltInTheWells.id,
    rv: 2,
    linkTypeId: LINK_TYPE_IDS.contains,
    sourceId: ids.arcRisingWater.id,
    targetId: ids.questSaltInTheWells.id,
    position: 'Zz',
    isPrimary: true,
});
/** The Tidebell's two related_to links to the Lantern of Warding are one duplicate. */
export const EXAMPLE_LINK_WARNING = {
    kind: 'duplicate',
    linkIds: [ids.linkTidebellRelatedToLantern.id, ids.linkTidebellRelatedToLanternDuplicate.id],
    offers: ['merge', 'keep_both'],
};
/** The link type `links.createType`'s example makes. */
export const EXAMPLE_LINK_TYPE_CREATED = {
    id: EXAMPLE_ANSWER_IDS.linkTypeCreated,
    key: 'sworn_to',
    label: 'Sworn to',
    inverseLabel: 'Sworn by',
    sourceRule: {},
    targetRule: {},
    ordered: false,
    symmetric: false,
    structural: false,
    temporal: false,
    metaSchema: { type: 'object', properties: {}, required: [], additionalProperties: false },
    builtin: false,
    archived: false,
};
function ref(id, fid, title, typeKey) {
    return { id, fid, title, titlePending: false, typeKey, library: 'canon', isStub: false };
}
const SALT = ref(ids.questSaltInTheWells.id, 'SaltInTheWells01', 'Salt in the Wells', 'quest');
/** Captain Orla Venn's backlinks: the quest she gives. */
export const EXAMPLE_BACKLINKS = {
    relations: [{ link: EXAMPLE_LINK, other: SALT }],
    mentions: [],
    usedIn: [],
    variants: [],
};
/** The arc's first two quests, under The Rising Water. */
export const EXAMPLE_TREE = [
    {
        ...ref(ids.questMissingLamplighters.id, 'LanternQuest0001', 'Missing Lamplighters', 'quest'),
        canonStatus: 'canon',
        linkId: ids.linkArcContainsMissingLamplighters.id,
        position: 'a0',
        childCount: 0,
    },
    {
        ...SALT,
        canonStatus: 'canon',
        linkId: ids.linkArcContainsSaltInTheWells.id,
        position: 'a1',
        childCount: 0,
    },
];
const trashed = {
    status: 409,
    body: { code: 'document_trashed', message: 'The document is in the trash.' },
};
/** CS r3: a validity boundary the server cannot use, named by `details.path` (1.C stage 2). */
const badBoundary = (path) => ({
    status: 422,
    body: {
        code: 'invalid_body',
        message: 'The boundary cannot be used.',
        details: { path: [...path] },
    },
});
/**
 * The answers CS r1c added to the link writes (DECISIONS 89 items 1 and 2), and CS r3's 422
 * `invalid_body` on `links.create` and `links.update`, one example each. The
 * sentences are examples: a client reads `code`, never `message`. A home chain too long to check is
 * refused as a cycle with its own sentence (DECISIONS 89 item 10).
 */
export const EXAMPLE_LINK_REFUSALS = {
    'links.create': [
        {
            ...trashed,
            request: {
                params: { id: ids.noteTornLedger.id },
                body: { linkTypeKey: 'related_to', targetId: ids.npcMaelisDorn.id },
            },
        },
        // CS r3: a placement edge whose placement is unknown, removed or on a trashed page.
        badBoundary(['validFrom', 'placementId']),
    ],
    'links.update': [
        trashed,
        // CS r3: a time point the calendar cannot read.
        badBoundary(['validTo', 'timePoint']),
        {
            status: 422,
            body: { code: 'primary_not_structural', message: 'Only a structural link can be a home.' },
            request: {
                params: { id: ids.questSaltInTheWells.id, linkId: ids.linkSaltGivenByOrla.id },
                body: { baseRv: 1, isPrimary: true },
            },
        },
        {
            status: 422,
            body: { code: 'link_cycle', message: 'That home would put the page inside itself.' },
        },
        { status: 422, body: { code: 'link_cycle', message: 'The home chain is too deep to check.' } },
    ],
    'links.remove': [trashed],
    'links.reorder': [trashed],
};
//# sourceMappingURL=examples-links.js.map